import React, { useCallback, useRef, useState } from 'react';
import { css } from '@compiled/react';

import { PopoverProvider } from '@atlaskit/spotlight/popover-provider';
import { PopoverTarget } from '@atlaskit/spotlight/popover-target';

import { PlacementFetchProvider } from '@confluence/confluence-post-office-provider/entry-points/placement-fetch-provider.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { TransparentErrorBoundary } from '@confluence/error-boundary/entry-points/TransparentErrorBoundary.ts';
import { UNSAFE_isExperimentEnabledNoExposure } from '@confluence/feature-experiments';

import { ReviewWelcomeNudge } from './ReviewWelcomeNudge.tsx';
import {
	ReviewContextualNudgeEligibility,
	type ReviewContextualNudgeContext,
} from './ReviewContextualNudgeEligibility.tsx';

// Wraps the button without adding a box, so it only observes clicks.
const targetStyles = css({ display: 'contents' });

// `done` once a nudge has left the screen, so no other nudge follows it.
type NudgeStatus = 'idle' | 'visible' | 'done';

export type ReviewButtonNudgeContext = ReviewContextualNudgeContext;

type ReviewButtonNudgeProps = {
	/** Receives whether a nudge is on screen, so the button can drop its tooltip rather than sit under the nudge. */
	children: (state: { isNudgeVisible: boolean }) => React.ReactElement;
	isVisible: boolean;
	onGetSuggestions?: () => void;
	context?: ReviewButtonNudgeContext;
};

/**
 * Anchors Post Office spotlight nudges to the Review suggestions button: a "Suggested edits" welcome
 * nudge and, when there is no welcome message, a richer contextual nudge for eligible pages. The
 * button stays in a stable tree so it never remounts when a message arrives, and the nudges are
 * optional UI that fails closed.
 *
 * At most one nudge is shown while this button stays mounted. The contextual nudge is the welcome
 * nudge's fallback, so the welcome nudge takes precedence, and once either nudge has left the screen
 * neither is rendered again.
 */
export const ReviewButtonNudge = ({
	children,
	isVisible,
	onGetSuggestions,
	context,
}: ReviewButtonNudgeProps): React.JSX.Element => {
	const [status, setStatus] = useState<NudgeStatus>('idle');
	const targetClickListenersRef = useRef(new Set<() => void>());

	const subscribeToTargetClick = useCallback((listener: () => void) => {
		const listeners = targetClickListenersRef.current;
		listeners.add(listener);
		return () => {
			listeners.delete(listener);
		};
	}, []);

	const handleShown = useCallback(() => setStatus('visible'), []);
	const handleHidden = useCallback(() => setStatus('done'), []);

	// Clicking Review is the one interaction that settles either nudge.
	const handleTargetClick = useCallback(() => {
		targetClickListenersRef.current.forEach((listener) => listener());
	}, []);

	// Post Office records the narrow experiment exposure when it actually shows a message, so this
	// only decides whether to ask for one and must not record an exposure of its own.
	if (
		!isVisible ||
		!UNSAFE_isExperimentEnabledNoExposure('platform_editor_ai_suggested_edits_nudges')
	) {
		return children({ isNudgeVisible: false });
	}

	return (
		<PopoverProvider>
			<PopoverTarget>
				<span css={targetStyles} onClickCapture={handleTargetClick}>
					{children({ isNudgeVisible: status === 'visible' })}
				</span>
			</PopoverTarget>
			<TransparentErrorBoundary attribution={Attribution.CC_EDITOR_AI}>
				<PlacementFetchProvider>
					{status !== 'done' && (
						<ReviewWelcomeNudge
							onShown={handleShown}
							onHidden={handleHidden}
							subscribeToTargetClick={subscribeToTargetClick}
							fallback={
								<ReviewContextualNudgeEligibility
									context={context}
									onShown={handleShown}
									onHidden={handleHidden}
									onGetSuggestions={onGetSuggestions}
									subscribeToTargetClick={subscribeToTargetClick}
								/>
							}
						/>
					)}
				</PlacementFetchProvider>
			</TransparentErrorBoundary>
		</PopoverProvider>
	);
};
